import { X } from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import "./touch-sheet.css";

let scrollLocks = 0;
let restoreScroll: (() => void) | null = null;
function lockBackgroundScroll() {
  if (scrollLocks++ === 0) {
    const body = document.body, root = document.documentElement, x = window.scrollX, y = window.scrollY;
    const previous = { position: body.style.position, top: body.style.top, left: body.style.left,
      width: body.style.width, overflow: body.style.overflow, rootOverflow: root.style.overflow };
    body.style.position = "fixed"; body.style.top = `${-y}px`; body.style.left = `${-x}px`;
    body.style.width = "100%"; body.style.overflow = "hidden"; root.style.overflow = "hidden";
    restoreScroll = () => {
      body.style.position = previous.position; body.style.top = previous.top; body.style.left = previous.left;
      body.style.width = previous.width; body.style.overflow = previous.overflow; root.style.overflow = previous.rootOverflow;
      window.scrollTo(x, y);
    };
  }
  return () => { if (--scrollLocks === 0) { restoreScroll?.(); restoreScroll = null; } };
}
const bounds = () => ({ height: window.visualViewport?.height ?? window.innerHeight,
  width: window.visualViewport?.width ?? window.innerWidth,
  top: window.visualViewport?.offsetTop ?? 0, left: window.visualViewport?.offsetLeft ?? 0 });

export function TouchSheet({ title, close, closeDisabled = false, children, actions, variant = "default" }: {
  title: string; close?: () => void; closeDisabled?: boolean; children: ReactNode; actions?: ReactNode;
  variant?: "default" | "planning" | "downtime" | "decision" | "details" | "quantity" | "machine" | "operator-menu";
}) {
  const id = useId(), dialog = useRef<HTMLDialogElement>(null), body = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null), closeRef = useRef({ close, closeDisabled });
  closeRef.current = { close, closeDisabled };
  const [viewport, setViewport] = useState(bounds);
  const keyboardOpen = window.innerHeight - viewport.height > 120;
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockBackgroundScroll();
    // The supported tablet baseline is Safari 15.4+ / Chromium with native dialog.
    // Native modal dialogs supply top-layer stacking and document-wide inertness.
    element.showModal();
    titleRef.current?.focus({ preventScroll: true });
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewport(bounds());
        const focused = document.activeElement, scroll = body.current;
        if (!(focused instanceof HTMLElement) || !scroll?.contains(focused)) return;
        const field = focused.getBoundingClientRect(), visible = scroll.getBoundingClientRect();
        if (field.bottom > visible.bottom - 12) scroll.scrollTop += field.bottom - visible.bottom + 12;
        else if (field.top < visible.top + 12) scroll.scrollTop -= visible.top + 12 - field.top;
      });
    };
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update); element.addEventListener("focusin", update);
    update();
    return () => {
      window.cancelAnimationFrame(frame); element.close(); unlock();
      window.visualViewport?.removeEventListener("resize", update); window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update); element.removeEventListener("focusin", update);
      if (previousFocus?.isConnected && !previousFocus.closest("[inert]")) previousFocus.focus({ preventScroll: true });
    };
  }, []);
  useLayoutEffect(() => {
    const focused = document.activeElement, scroll = body.current;
    if (!(focused instanceof HTMLElement) || !scroll?.contains(focused)) return;
    const field = focused.getBoundingClientRect(), visible = scroll.getBoundingClientRect();
    if (field.bottom > visible.bottom - 12) scroll.scrollTop += field.bottom - visible.bottom + 12;
    else if (field.top < visible.top + 12) scroll.scrollTop -= visible.top + 12 - field.top;
  }, [viewport]);
  const style: CSSProperties = { top: viewport.top, left: viewport.left, width: viewport.width, height: viewport.height };
  return createPortal(<dialog ref={dialog} className={`touch-modal touch-sheet-backdrop is-${variant}${keyboardOpen ? " is-keyboard-open" : ""}`}
    style={style} aria-labelledby={id} aria-modal="true"
    onCancel={(event) => { event.preventDefault(); if (!closeRef.current.closeDisabled) closeRef.current.close?.(); }}
    onKeyDown={(event) => {
      if (event.defaultPrevented || event.key !== "Tab") return;
      const modal: HTMLDialogElement = event.currentTarget;
      const elements = Array.from(modal.querySelectorAll<HTMLElement>("button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex]:not([tabindex='-1'])"))
        .filter((item) => item.getClientRects().length > 0 && getComputedStyle(item).visibility !== "hidden" && !item.inert);
      const first = elements[0], last = elements.at(-1);
      if (!first || !last) { event.preventDefault(); titleRef.current?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === titleRef.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }}>
    <section className={`touch-sheet ${variant === "default" ? "" : `${variant}-sheet`}`}>
      <header><div>{variant === "default" || variant === "downtime" ? <small>ÜRETİM HAREKETİ</small> : null}
        <h2 ref={titleRef} id={id} tabIndex={-1}>{title}</h2></div>
        {close ? <button type="button" aria-label="Kapat" disabled={closeDisabled} onClick={close}><X size={22} aria-hidden="true" /></button> : null}
      </header>
      <div ref={body} className="touch-sheet-body">{children}</div>
      {actions ? <div className="touch-sheet-actions">{actions}</div> : null}
    </section>
  </dialog>, document.body);
}
